import { LinePriceDetails } from '../../../../components/lines-editor/base-doc-line-editor/base-doc-line-editor';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Injector,
  Input,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable, firstValueFrom } from 'rxjs';
import {
  BaseThirdPartyDocLineEditor,
  LinesEditorComponent,
  makeDocLineProductionCol,
} from '../../../../components';
import {
  CustomerSalesPurchaseDto,
  DocLineRole,
  DocLineUpdatedDto,
  CustomerSalesPurchaseService,
  ReservationResult,
} from '@digiwall/goliath-documents/proxy';
import { VatType } from '@digiwall/goliath-core/proxy';
import { FieldTypes, IHtmlCellRendererParams } from '@digiwall/ag-grid';
import { ShapingService } from '../../../../services';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { ObserverQueue } from '@digiwall/core';
import { DocType } from '../../../base-docs/detail/doc-type';
import { AskLocationModalComponent } from '../../../../components/ask-location/ask-location-modal.component';
import { StockFeatureDefinition } from '@digiwall/goliath-products/config';
import { ColorizedIcon, ColorizedIconHelper } from '../../../model/colorized-icon.model';

@Component({
  selector: 'document-customer-sales-purchases-lines-grid',
  templateUrl: './customer-sales-purchase-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class CustomerSalesPurchaseLinesEditorComponent extends BaseThirdPartyDocLineEditor<CustomerSalesPurchaseDto> {
  protected readonly stockFeature = inject(StockFeatureDefinition);

  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  @Input() set salesPurchase(value) {
    this._document = value;
    this.setLanguageCode();

    if (value != null) {
      this.setActionButtons();
    }
  }

  public readonly = input<boolean>(false);

  public reservationUpdatedEvent = output<void>();
  public linePricingDetailsEvent = output<LinePriceDetails>();
  public entityUpdateRequested = output<void>();

  public columnDefs: Array<ColDef> = [];
  public actionBtns: any[] = [];

  protected articleLineRole: DocLineRole = DocLineRole.SalesPurchaseArticle;
  protected textLineRole: DocLineRole = DocLineRole.CustomerDocText;
  protected chapterLineRole: DocLineRole = DocLineRole.SalesPurchaseChapter;

  protected override handlingLinesUpdatedEvent = true;
  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticleSellableSelectionLookup(term);

  private computationModuleActive: boolean = false;

  constructor(
    localizationService: LocalizationService,
    private salesPurchaseService: CustomerSalesPurchaseService,
    private shapingService: ShapingService,
    private confirmationService: ConfirmationService,
    injector: Injector,
    updateQueue: ObserverQueue,
    protected toastService: ToasterService,
  ) {
    super(localizationService, salesPurchaseService, injector, updateQueue);

    this.initColDefs();
  }

  protected override colDef_StockStatusIcons: ColDef = {
    colId: 'stockStatusIcons',
    pinned: 'left',
    width: 60,
    type: FieldTypes.Html,
    cellRendererParams: {
      src: (p: ICellRendererParams) => {
        if (p.data != null) {
          let htmlClasses: Array<ColorizedIcon> = [];
          if (p.data.deliveredQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-truck1', color: 'green' });
          }
          if (p.data.reservedQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-stock-lock ', color: 'blue' });
          }
          if (p.data.backorderQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-triangle', color: 'orange' });
          }
          if (p.data.cancelledQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-impossible', color: 'red' });
          }
          if (p.data.hasOrderLineLink) {
            htmlClasses.push({ icon: 'digi-out-green-factory', color: 'darkred' });
          }
          return ColorizedIconHelper.toHtml(htmlClasses);
        }
        return '';
      },
    } as IHtmlCellRendererParams,
  };

  private setActionButtons(): void {
    let newActionBtns = [];

    if (this._document?.isPartiallyDelivered) {
      newActionBtns.push({
        label: this.localizationService.instant('Doc::CancelQuantityForDelivery'),
        action: () => {
          this.cancelLinesDelivery();
        },
        disabled: computed(() => {
          return this.selectedLines().some((x: any) => x.toDeliverQuantity == 0);
        }),
      });
    }
    if (this.stockFeature.isEnabled) {
      newActionBtns.push(
        ...[
          {
            label: this.localizationService.instant('Doc::ManualReservation'),
            action: () => {
              this.reserveLines();
            },
            disabled: computed(() => {
              return this.selectedLines().length <= 0;
            }),
          },
          {
            label: this.localizationService.instant('Doc::CancelReservation'),
            action: () => {
              this.unreserveLines();
            },
            disabled: computed(() => {
              return this.selectedLines().length <= 0;
            }),
          },
        ],
      );
    }

    newActionBtns.push({
      label: this.localizationService.instant('Doc::CustomerSalesPurchase:UnlinkSupplierOrderLine'),
      action: () => {
        this.unlinkSupplierOrderLines();
      },
      disabled: computed(() => {
        return this.selectedLines().length <= 0;
      }),
    });

    this.actionBtns.splice(0, this.actionBtns.length, ...newActionBtns);
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.splice(
      this.columnDefs.findIndex(colDef => colDef.colId === BaseThirdPartyDocLineEditor.CODE_COL_ID),
      0,
      this.colDef_StockStatusIcons,
    );
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_NetUnitPrice,
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
        {
          field: 'toDeliverQuantity',
          width: 120,
          headerName: this.localizationService.instant(
            'Doc::CustomerSalesPurchase:ToDeliverQuantity',
          ),
          editable: false,
          type: FieldTypes.Number,
          cellRendererParams: {
            decimals: this.currentCompanyStore.quantityDecimals,
          },
        },
        {
          field: 'deliveredQuantity',
          width: 120,
          headerName: this.localizationService.instant(
            'Doc::CustomerSalesPurchase:DeliveredQuantity',
          ),
          editable: false,
          type: FieldTypes.Number,
          cellRendererParams: {
            decimals: this.currentCompanyStore.quantityDecimals,
          },
        },
        this.coldef_CancelledQuantity,
        ...(this.stockFeature.isEnabled
          ? [
              {
                field: 'toDeliverQuantityInStockUnit',
                width: 120,
                headerName: this.localizationService.instant(
                  'Doc::CustomerSalesPurchase:ToDeliverQuantityInStockUnit',
                ),
                editable: false,
                hide: true,
                type: FieldTypes.Number,
                cellRendererParams: {
                  decimals: this.currentCompanyStore.quantityDecimals(),
                },
              },
              {
                field: 'deliveredQuantityInStockUnit',
                width: 120,
                headerName: this.localizationService.instant(
                  'Doc::CustomerSalesPurchase:DeliveredQuantityInStockUnit',
                ),
                editable: false,
                hide: true,
                type: FieldTypes.Number,
                cellRendererParams: {
                  decimals: this.currentCompanyStore.quantityDecimals(),
                },
              },
              this.getColDef_CancelledQuantityStock(),
            ]
          : []),
        makeDocLineProductionCol(
          k => this.localizationService.instant(k),
          e => this.isCellEditable(e),
          row => {
            this.computationModuleActive = true;
            this.openComputation(row);
          },
        ),
      ] as Array<ColDef>),
    );
  }

  isChapter = line => line.lineRole == DocLineRole.SalesPurchaseChapter;

  public override isCellEditable = (params: EditableCallbackParams<any, any>) => {
    return (
      super.isCellEditable(params) &&
      !params.data.hasOrderLineLink &&
      !(this._document as CustomerSalesPurchaseDto).isPartiallyDelivered &&
      !this.computationModuleActive
    );
  };

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this.readonly()) return [];

    let menuItems: (string | MenuItemDef<any, any>)[];
    menuItems = this.contextMenuItems(params);

    let line = params?.node?.data;
    if (line?.computationModuleEnable) {
      menuItems.push('separator', {
        name: this.localizationService.instant('Shaping::ComputationModule'),
        action: _ => {
          this.openComputation(line);
        },
      });
    }

    return menuItems;
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.salesPurchaseService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
            true,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.salesPurchaseService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.salesPurchaseService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
            true,
          );
          break;
        case 'unitWeight':
          observable = this.salesPurchaseService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };

  openComputation = (line): void => {
    this.shapingService.openComputation({
      docId: this._document.id,
      concurrencyStamp: this._document.concurrencyStamp,
      thirdPartyId: this._document.thirdPartyId,
      line: line,
      docService: this._docService,
      docLanguageId: this._document.languageId,
      callbackFn: result => {
        this.computationModuleActive = false;
        if (result) {
          if (result.errors?.length > 0) {
            this.confirmationService
              .warn(
                result.errors.join('\n'),
                this.localizationService.instant('Shaping::ComputationModule:ErrorsOccurred'),
                { hideCancelBtn: true, yesText: this.localizationService.instant('::Close') },
              )
              .subscribeOnce();
          }

          this.removeChildren(line.id);
          this._updateLines(result);
        }
      },
    });
  };

  private async wantToCancelQuantityForDelivery() {
    let status = await firstValueFrom(
      this.confirmationService.warn('Doc::WantToCancelQuantityForDelivery', '::AreYouSure'),
    );
    return status == Confirmation.Status.confirm;
  }

  protected async cancelLinesDelivery() {
    const ids = this.selectedLines()
      .filter(line => line.lineRole == DocLineRole.SalesPurchaseArticle)
      .map(line => line.id);
    if (ids == null) {
      return;
    }

    if (await this.wantToCancelQuantityForDelivery()) {
      this._updateQueue
        .enqueue(() => this.salesPurchaseService.cancelLinesDelivery(this._document.id, ids))
        .subscribe(result => {
          this.toastService.success('Doc::CancelQuantityToDeliverSuccessfully');
          this.lineUpdatedEvent.emit({
            concurrencyStamp: this._document.concurrencyStamp,
            isClosed: result.isClosed,
          });
          this._updateLines(result);
        });
    }
  }

  protected async unreserveLines() {
    const ids = this.selectedLines()
      .filter(line => line.lineRole == DocLineRole.SalesPurchaseArticle)
      .map(line => line.id);
    if (ids == null) {
      return;
    }

    this.salesPurchaseService.unreserveLines(this._document.id, ids).subscribeOnce(result => {
      if (result == ReservationResult.NotReserved || result == ReservationResult.NothingToReserve) {
        this.toastService.success('Doc::CustomerSalesPurchase:UnreservationEnded');
      } else {
        this.toastService.warn('Doc::CustomerSalesPurchase:UnreservationFailed');
      }
      this.reservationUpdatedEvent.emit();
    });
  }

  protected async reserveLines() {
    const ids = this.selectedLines()
      .filter(line => line.lineRole == DocLineRole.SalesPurchaseArticle)
      .map(line => line.id);
    if (ids == null) {
      return;
    }

    const defaultLocationId = await firstValueFrom(
      this.salesPurchaseService.getDefaultLocationId(this._document.id, ids),
    );
    const modalRef = this.modalService.open(AskLocationModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      injector: this.injector,
    });

    modalRef.shown.subscribeOnce(() => {
      modalRef.componentInstance.defaultLocationId.set(defaultLocationId);
    });

    modalRef.closed.subscribeOnce(preferredLocationId => {
      if (preferredLocationId != null) {
        this.salesPurchaseService
          .manualReserveLines(this._document.id, preferredLocationId, ids)
          .subscribeOnce(result => {
            if (result == ReservationResult.Reserved) {
              this.toastService.success('Doc::CustomerSalesPurchase:ReservationSuccessful');
            } else if (result == ReservationResult.PartiallyReserved) {
              this.toastService.success(
                'Doc::CustomerSalesPurchase:ReservationPartiallySuccessful',
              );
            } else {
              this.toastService.warn('Doc::CustomerSalesPurchase:ReservationFailed');
            }
            this.reservationUpdatedEvent.emit();
          });
      }
    });
  }

  private unlinkSupplierOrderLines() {
    this.salesPurchaseService
      .unlinkSupplierOrderLines(this.selectedLines().map(line => line.id))
      .subscribeOnce(deletedLinkCount => {
        this.toastService.success(
          this.localizationService.instant(
            'Doc::CustomerSalesPurchase:UnlinkSupplierOrderLineSuccessful',
            deletedLinkCount.toString(),
          ),
        );
        this.entityUpdateRequested.emit();
      });
  }
}
